import { useDispatch, useSelector } from "react-redux";
import { setSnackbar } from "reduxStore/state";
import { updateUser } from "reduxStore/actions/userActions";
import * as Yup from "yup";
import { useEffect, useState } from "react";
import { debounce } from "lodash";
import { searchOrganisations } from "reduxStore/actions/organisationActions";
import { registerUser } from "reduxStore/actions/authActions";
import { fetchRoles } from "reduxStore/actions/roleActions";

export const useUserForm = ({ editMode, setOpen, currentUser }) => {
  const dispatch = useDispatch();

  const organisationSearchResults =
    useSelector((state) => state.organisationSearchResults) || [];
  const [organisationSearchTerm, setOrganisationSearchTerm] = useState("");

  const roles = useSelector((state) => state.roles);

  const initialValues = {
    firstName: currentUser ? currentUser.firstName : "",
    lastName: currentUser ? currentUser.lastName : "",
    email: currentUser ? currentUser.email : "",
    roles: currentUser ? currentUser.roles : [],
    organisation: currentUser?.organisation,
    sendUserWelcomeEmail: false,
  };

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required("First Name is required"),
    lastName: Yup.string().required("Last Name is required"),
    email: Yup.string()
      .email("Enter a valid email")
      .required("Email is required"),
    // password: Yup.string(),
    roles: Yup.array().min(1, "At least one role is required"),
    organisation: Yup.object().nullable().required("Organisation is required"), // Change validation for singular value
    sendUserWelcomeEmail: Yup.boolean(),
  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      values.organisation = values.organisation._id;
      const userId = currentUser?._id;
      values.roles = values.roles.map((role) => role._id);

      const response = editMode
        ? await dispatch(updateUser(userId, values))
        : await dispatch(
            registerUser({
              ...values,
              sendUserWelcomeEmail: values.sendUserWelcomeEmail,
            })
          );

      console.log("🧪 update/register response:", response);

      if (response.payload) {
        dispatch(
          setSnackbar({
            open: true,
            message: editMode
              ? "User updated successfully"
              : "User created successfully",
            severity: "success",
          })
        );
        setOpen(false);
        resetForm();
      } else {
        dispatch(
          setSnackbar({
            open: true,
            message: editMode ? "User update failed" : "User creation failed",
            severity: "error",
          })
        );
      }
    } catch (error) {
      console.error(error);

      dispatch(
        setSnackbar({
          open: true,
          message: "Something went wrong",
          severity: "error",
        })
      );
    } finally {
      setSubmitting(false);
      setOpen(false);
    }
  };

  useEffect(() => {
    dispatch(fetchRoles());
  }, [dispatch]);

  // debounce input search term for organisation
  useEffect(() => {
    const debounceFetch = debounce(() => {
      if (organisationSearchTerm) {
        dispatch(searchOrganisations(organisationSearchTerm));
      }
    }, 300); // 300ms delay
    debounceFetch();

    // Cleanup function to cancel the debounce on unmount
    return () => {
      debounceFetch.cancel();
    };
  }, [organisationSearchTerm, dispatch]);

  return {
    validationSchema,
    initialValues,
    handleSubmit,
    organisationSearchResults,
    setOrganisationSearchTerm,
    roles,
  };
};
